import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { openModalAction } from '../../../../actions/modal';
import Strings from '../../../../Strings';
import DashboardLayout from '../../../Dashboards/DashboardLayout';
import { actions } from '../redux/actions';
import { actions as scheduleActions } from '../../Schedules/redux/actions';
import { EDIT_PATIENT_RESULT, GET_CAPS_WITHOUT_SCHEDULE_RESULT, GET_CONDITION_LIST_RESULT } from '../redux/constants';
import { actions as cohortActions } from '../Cohorts/redux/actions';
import { CREATE_CONDITION_RESULT } from '../Cohorts/redux/constants';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import MedicationWidget from './MedicationWidget';
import AdherenceGraph from '../AdherenceGraph';
import { ADD_SCHEDULE, EDIT_SCHEDULE } from '../../Schedules/redux/constants';
import { getProgramsFromPatient } from '../../../../utils/cmsPrograms';
import { ORG_FEATURES, PERMISSIONS, hasPermission, orgFeatureEnabled } from '../../../../utils/userPermissions';
import { RPM_PATIENT_STATUS } from '../../../../constants';
import { buildMedicationRows, createConditionOptionsLoader } from '../../../../utils/rpmPatientFields';
import { getEditablePatient, getMedicationRemoval, getMedicationUpdate, getMissingConditions } from './patientMedicationEdit';
import MedicationsTable from './MedicationsTable';
import { assignCapConfirmation, deleteScheduleConfirmation, unassignCapConfirmation } from './scheduleDetails';
import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';

export function getWidgetsMetadata(meds) {
  return meds && meds.length > 0 ? meds.map(medication => getWidgetMetadataForMed(medication)) : [];
}

function getWidgetMetadataForMed(medication) {
  return {
    id: `MedicationWidget_${medication.rootId}`,
    name: medication.medicationName,
    permission: '',
    defProps: {
      i: `MedicationWidget_${medication.rootId}`,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 3,
      maxH: 1,
    },
  };
}

const L_WIDGET_COUNT = 3;
const M_WIDGET_COUNT = 2;
const S_WIDGET_COUNT = 1;

export function getDefaultLayouts(meds, permissions) {
  const widgets = getWidgetsMetadata(meds);
  const l = [];
  const m = [];
  const s = [];

  widgets
    .filter(w => !w.permission || permissions?.includes(w.permission))
    .forEach((w, i) => {
      l.push({ ...w.defProps, x: i % L_WIDGET_COUNT, y: Math.floor(i / L_WIDGET_COUNT), w: 1, h: 1 });
      m.push({ ...w.defProps, x: i % M_WIDGET_COUNT, y: Math.floor(i / M_WIDGET_COUNT), w: 1, h: 1 });
      s.push({ ...w.defProps, x: i % S_WIDGET_COUNT, y: Math.floor(i / S_WIDGET_COUNT), w: 1, h: 1 });
    });
  return { l, m, s };
}

export const PATIENT_MEDICATION_DASHBOARD_ID_PREFIX = 'dashboardPatientMedications1_';

function PatientMedications({ patientVersion = false, ...props }) {
  const { patientMedications, isLoading } = props;
  const patientId = props.patientId || props.userId;
  const engagementMessagesEnabled = orgFeatureEnabled(ORG_FEATURES.engagement_messages_enabled);

  const DASHBOARD_ID = `${PATIENT_MEDICATION_DASHBOARD_ID_PREFIX}${patientId}`;
  const isPatientDisenrolled = props.patientDetails?.enrollmentStates?.every(state => state === RPM_PATIENT_STATUS.ended);
  const canEdit = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_MEDICATION_EDIT) && !isPatientDisenrolled;

  const [medications, setMedications] = useState();
  const [capsWithoutSchedule, setCapsWithoutSchedule] = useState([]);
  // Row whose schedule is plotted: undefined = not chosen yet (the first scheduled row is shown).
  const [selectedRowKey, setSelectedRowKey] = useState(undefined);
  const scrollbarRef = React.useRef();

  useEffect(() => {
    props.getMedications(patientId);
    props.getSchedules(patientId, { adherence: true, compact: false});
    if (!props.patientDetails) {
      props.getPatientInfo(patientId);
    }
    if (!patientVersion) {
      getCaps();
    }
  }, [patientId, props.refreshTimestamp]);

  useEffect(() => {
    if (_.isEqual(patientMedications, medications)) {
      return;
    }

    setMedications(patientMedications);
    if (patientMedications?.length > 0 && !patientVersion) {
      getCaps();
    }
  }, [patientMedications]);

  const getCaps = () => {
    props.getCaps(patientId).then(r => {
      if (r.type === GET_CAPS_WITHOUT_SCHEDULE_RESULT) {
        const caps = r.response;
        if (caps) {
          setCapsWithoutSchedule(caps);
        }
      }
    });
  };

  // Schedule cards: the patient view only (staff see the table).
  const widgets = patientVersion && patientMedications?.length > 0
    ? patientMedications.map(medication => ({
      ...getWidgetMetadataForMed(medication),
      render: (
        <MedicationWidget
          schedule={medication}
          devices={capsWithoutSchedule}
          patient={props.patientDetails}
          patientEnrollments={getProgramsFromPatient(props.patientDetails)}
          hideActions={!canEdit}
          simplifiedVersion={patientVersion}
        />
      ),
      noPadding: true,
    }))
    : [];

  // Staff view: schedules joined with the patient's medications list (Medical history); only once both have loaded,
  // otherwise every medication would look unscheduled, or the list rows would join later and shift the table.
  const medicationRowsReady = !patientVersion && props.patientMedicationsLoaded && !!props.patientDetails;
  const medicationRows = React.useMemo(
    () => (medicationRowsReady ? buildMedicationRows(patientMedications, props.patientDetails.medications) : []),
    [medicationRowsReady, patientMedications, props.patientDetails?.medications],
  );
  // A row that went away (e.g. its schedule was removed) falls back like no choice yet: the first row with a plot.
  const selectedRow = medicationRows.find(r => r.key === selectedRowKey)
    || medicationRows.find(r => r.schedule)
    || medicationRows[0];
  const patientEnrollments = React.useMemo(() => getProgramsFromPatient(props.patientDetails), [props.patientDetails]);
  const canEditMedicalHistory = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT);

  const onEditSchedule = React.useCallback(schedule => props.onEditSchedule(props.patientDetails, schedule), [props.patientDetails]);
  const onRemoveSchedule = React.useCallback(
    schedule => props.openConfirmModal(deleteScheduleConfirmation(props.patientDetails.id, schedule)),
    [props.patientDetails],
  );
  const onAddSchedule = React.useCallback(
    patientMedication => props.onAddMedicine(props.patientDetails, patientMedication),
    [props.patientDetails],
  );
  const onAttachCap = React.useCallback(
    (schedule, capId) => props.openConfirmModal(assignCapConfirmation(props.patientDetails.id, schedule, capId)),
    [props.patientDetails],
  );
  const onDetachCap = React.useCallback(
    schedule => props.openConfirmModal(unassignCapConfirmation(props.patientDetails.id, schedule)),
    [props.patientDetails],
  );
  // Saves an entry edited in the dialog: creates the ICD conditions it introduced, then updates the patient's
  // medications list (and conditions, when some were added).
  const saveEditedMedication = async (patient, updatedMedication, medIndex, medicationConditions) => {
    const missingConditions = getMissingConditions(patient, medicationConditions);
    // One at a time, like the wizard; stops at the first failure (null).
    const createdConditions = await missingConditions.filter(c => !c.id && c.code).reduce(async (previous, condition) => {
      const created = await previous;
      if (!created) {
        return null;
      }
      const response = await props.createCondition({ icd10cm: condition.code });
      if (response?.type !== CREATE_CONDITION_RESULT) {
        props.showNotification(response?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        return null;
      }
      return [...created, { ...condition, id: response.response?.id }];
    }, Promise.resolve([]));
    if (!createdConditions) {
      return;
    }
    const update = getMedicationUpdate(patient, updatedMedication, medIndex, missingConditions, createdConditions);
    const response = await props.editPatient(update, patient.id);
    if (response?.type !== EDIT_PATIENT_RESULT) {
      props.showNotification(response?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
      return;
    }
    props.showNotification(Strings.success.medicationSaved);
    props.getPatientInfo(patient.id);
    props.getMedications(patient.id);
  };

  // Opens the Medical History medication dialog on its own: for this entry, or for a new one when patientMedication is
  // undefined (rows hold the patient details' own medication objects).
  const openMedicalHistoryMedication = async patientMedication => {
    const medIndex = patientMedication ? props.patientDetails?.medications?.indexOf(patientMedication) : undefined;
    if (medIndex !== undefined && medIndex < 0) {
      return;
    }
    const conditionsResponse = await props.getConditions();
    const orgConditions = conditionsResponse?.type === GET_CONDITION_LIST_RESULT
      ? conditionsResponse.response.data?.filter(c => c.org_uuid === props.patientDetails.orgId || !props.patientDetails.orgId)
      : [];
    const patient = getEditablePatient(props.patientDetails, orgConditions);
    props.openEditMedication({
      medication: medIndex === undefined ? undefined : patient.medications[medIndex],
      medIndex,
      patient,
      orgConditions,
      loadConditionOptions: createConditionOptionsLoader(props.getIdcConditions, orgConditions),
      onConfirm: (updatedMedication, i, medicationConditions) => saveEditedMedication(patient, updatedMedication, i, medicationConditions),
    });
  };
  const onEditMedicalHistory = patientMedication => openMedicalHistoryMedication(patientMedication);
  const onAddMedicalHistoryMedication = () => openMedicalHistoryMedication();
  // Removes the entry from the patient's medications list after a confirmation. A schedule linked to it is kept and
  // shows as not in the medication list.
  const onRemoveFromMedicalHistory = (patientMedication, schedule) => {
    const medIndex = props.patientDetails?.medications?.indexOf(patientMedication);
    if (medIndex === undefined || medIndex < 0) {
      return;
    }
    const patientId = props.patientDetails.id;
    props.openConfirmModal({
      title: (
        <div>
          <div>{Strings.formatString(Strings.patient_medications.removeFromMedicalHistoryWarning, patientMedication.name)}</div>
          {schedule && <div>{Strings.patient_medications.removeFromMedicalHistoryScheduleKept}</div>}
        </div>
      ),
      hideCaution: true,
      onConfirmAction: actions.editPatient(getMedicationRemoval(props.patientDetails, medIndex), patientId),
      confirmPostAction: dispatch => {
        dispatch(actions.getPatientInfo(patientId));
        dispatch(actions.getMedications(patientId));
      },
      onSuccessNotification: Strings.patient_medications.removedFromMedicalHistory,
      onCancelAction: null,
    });
  };
  const onRowClick = React.useCallback(row => setSelectedRowKey(row.key), []);

  const getHeaderTitle = () => {
    if (isLoading || !patientMedications) {
      return Strings.showingWait;
    }
    if (medicationRows.length === 0) {
      return Strings.noMedications;
    }
    return Strings.formatString(
      Strings.showingX,
      medicationRows.length,
      medicationRows.length === 1 ? Strings.medication : Strings.medications,
    );
  };

  const breakpoints = {
    l: 1400,
    m: 1151,
    s: 0,
  };
  const columns = {
    l: L_WIDGET_COUNT,
    m: M_WIDGET_COUNT,
    s: S_WIDGET_COUNT,
  };

  return (
    <OverlayScrollbarsComponent
      defer
      className="scrollbar-right-margin"
      style={{ height: '100%' }}
      options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
      ref={scrollbarRef}
    >
      <div className="medication-details-page">
        {patientVersion && <h2>{Strings.yourMedications}</h2>}
        {!patientVersion && (
          <PageHeader
            isBlack
            left={getHeaderTitle()}
            right={
              <>
                {canEditMedicalHistory && (
                  <Button
                    class={HEADER_BUTTON_DARK_BLUE}
                    onClick={onAddMedicalHistoryMedication}
                    disabled={!props.patientDetails}
                    title={Strings.addMedication}
                    testid="add-medical-history-medication"
                  />
                )}
                {canEdit && (
                  <Button
                    class={HEADER_BUTTON_DARK_BLUE}
                    onClick={() => props.onAddMedicine(props.patientDetails)}
                    title={Strings.patient_medications.addScheduleTitle}
                    testid="add-medication"
                  />
                )}
              </>
            }
          />
        )}
        {patientVersion && widgets.length > 0 && !isLoading && (
          <DashboardLayout
            dashboardId={DASHBOARD_ID}
            widgets={widgets}
            defaultLayouts={getDefaultLayouts(patientMedications, props.permissions)}
            breakpoints={breakpoints}
            rowHeight={patientVersion ? 364 : engagementMessagesEnabled ? 524 : 482}
            cols={columns}
            additionalData={patientMedications}
            isReadOnly={patientVersion}
            scrollbarRef={scrollbarRef}
          />
        )}
        {!patientVersion && (
          <div className="patient-medications-unified">
            <div className="patient-medications-table">
              <MedicationsTable
                rows={medicationRows}
                devices={capsWithoutSchedule}
                patientEnrollments={patientEnrollments}
                patientConditions={props.patientDetails?.conditions}
                canEdit={canEdit}
                canEditMedicalHistory={canEditMedicalHistory}
                isLoading={isLoading || !medicationRowsReady}
                selectedRowKey={selectedRow?.key}
                onRowClick={onRowClick}
                onEditSchedule={onEditSchedule}
                onRemoveSchedule={onRemoveSchedule}
                onAddSchedule={onAddSchedule}
                onAttachCap={onAttachCap}
                onDetachCap={onDetachCap}
                onEditMedicalHistory={onEditMedicalHistory}
                onRemoveFromMedicalHistory={onRemoveFromMedicalHistory}
              />
            </div>
          </div>
        )}
        <div className="patient-medications-chart">
          {/* Staff view: plots the selected row's schedule; rows without one have nothing to plot. */}
          {!patientVersion && selectedRow?.schedule && (
            <AdherenceGraph includeSchedulesWithNoCap hideMedSelect medSelectedFromDashboard={selectedRow.schedule.rootId} patientId={patientId} />
          )}
        </div>
      </div>
    </OverlayScrollbarsComponent>
  );
}

PatientMedications.propTypes = {
  getMedications: PropTypes.func,
  isLoading: PropTypes.any,
  onAddMedicine: PropTypes.func,
  onEditSchedule: PropTypes.func,
  getPatientInfo: PropTypes.func,
  openEditMedication: PropTypes.func,
  getConditions: PropTypes.func,
  getIdcConditions: PropTypes.func,
  createCondition: PropTypes.func,
  editPatient: PropTypes.func,
  showNotification: PropTypes.func,
  openConfirmModal: PropTypes.func,
  getCaps: PropTypes.func,
  patientDetails: PropTypes.any,
  patientId: PropTypes.any,
  patientMedications: PropTypes.array,
  patientMedicationsLoaded: PropTypes.bool,
  patientVersion: PropTypes.bool,
  refreshTimestamp: PropTypes.any,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    patientMedications: patients?.patientMedications?.medications,
    pagination: patients?.patientMedications?.pagination,
    isLoading: patients?.isPatientMedicationsLoading,
    patientMedicationsLoaded: patients?.patientMedicationsLoaded,
    patientDetails: patients?.patientDetails,
    userId: state.auth.profile?.id,
    permissions: state.auth?.permissions,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getSchedules: (patientId, request) => dispatch(scheduleActions.getSchedules(patientId, request)),
  getMedications: patientId => dispatch(actions.getMedications(patientId)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  // patientMedication (optional): the patient medications list entry the new schedule starts linked to.
  onAddMedicine: (patient, patientMedication) =>
    dispatch(
      openModalAction('edit-rpm-schedule', {
        patient,
        action: scheduleActions.addSchedule,
        actionType: ADD_SCHEDULE,
        patientMedication,
        onSuccess: () => dispatch(actions.getMedications(patient.id)),
      }),
    ),
  onEditSchedule: (patient, schedule) =>
    dispatch(
      openModalAction('edit-rpm-schedule', {
        patient,
        schedule,
        actionType: EDIT_SCHEDULE,
        onSuccess: () => dispatch(actions.getMedications(patient.id)),
      }),
    ),
  openEditMedication: data => dispatch(openModalAction('edit-medication', data)),
  getConditions: () => dispatch(cohortActions.getConditions()),
  getIdcConditions: pageRequest => dispatch(cohortActions.getIdcConditions(pageRequest)),
  createCondition: data => dispatch(cohortActions.createCondition(data)),
  editPatient: (data, patientId) => dispatch(actions.editPatient(data, patientId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  getCaps: patientId => dispatch(actions.getCapsWithoutSchedule(patientId)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientMedications);
